import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, Calendar, User, Landmark, CheckCircle2, AlertCircle, Trash2, Edit2, CreditCard, Printer, FileSpreadsheet, Briefcase, FileText, Building, ArrowDownRight, Receipt } from 'lucide-react';
import { exportToExcel } from '../../utils/excelExport';
import { Card, Button, Input, Badge, Select } from '../../components/ui/index.jsx';
import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const BankList = () => {
    const [activeTab, setActiveTab] = useState('reports'); // 'reports' | 'partners' | 'expenses' | 'treasury'
    const [depositTypeModalOpen, setDepositTypeModalOpen] = useState(false);
    
    // Regular Bank Transactions State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    
    // Partner Deposits State
    const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
    const [editingPartnerDeposit, setEditingPartnerDeposit] = useState(null);

    // Expenses State
    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
    const [editingExpenseTransaction, setEditingExpenseTransaction] = useState(null);

    // Treasury Feed State
    const [isTreasuryModalOpen, setIsTreasuryModalOpen] = useState(false);
    const [editingTreasury, setEditingTreasury] = useState(null);

    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [printDates, setPrintDates] = useState({
        from: new Date().toISOString().split('T')[0],
        to: new Date().toISOString().split('T')[0]
    });
    
    const [searchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const { success, error: toastError } = useToast();
    const [currentPage, setCurrentPage] = useState(1);
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('bank', 'create');

    useEffect(() => {
        const querySearch = searchParams.get('search');
        if (querySearch !== null) {
            setSearchTerm(querySearch);
            setCurrentPage(1);
        }
    }, [searchParams]);

    const { data: transactionsResponse, loading: txLoading, refresh: refreshTx } = useGet(`/bank-transactions?type=deposit&page=${activeTab === 'reports' ? currentPage : 1}&search=${searchTerm}`);
    const { data: expensesResponse, loading: expLoading, refresh: refreshExp } = useGet(`/bank-transactions?type=expense&page=${activeTab === 'expenses' ? currentPage : 1}&search=${searchTerm}`);
    const { data: partnersResponse, loading: pdLoading, refresh: refreshPd } = useGet(`/partner-deposits?page=${activeTab === 'partners' ? currentPage : 1}&search=${searchTerm}`);
    const { data: treasuryResponse, loading: trLoading, refresh: refreshTr } = useGet(`/bank-transactions?type=treasury_feed&page=${activeTab === 'treasury' ? currentPage : 1}&search=${searchTerm}`);

    const transactions = transactionsResponse?.data || [];
    const txPagination = transactionsResponse?.meta || null;
    const expenseTransactions = expensesResponse?.data || [];
    const expPagination = expensesResponse?.meta || null;
    const partnerDeposits = partnersResponse?.data || partnersResponse || [];
    const pdPagination = partnersResponse?.meta || null;
    const treasuryFeeds = treasuryResponse?.data || [];
    const trPagination = treasuryResponse?.meta || null;

    const stats = transactionsResponse?.stats || { total_count: 0, total_amount: 0, bank_operations_amount: 0, cheque_amount: 0, espece_amount: 0 };
    const [printLoading, setPrintLoading] = useState(false);

    const handleExportExcel = async () => {
        try {
            if (activeTab === 'reports') {
                const response = await api.get(`/bank-transactions?all=true&type=deposit&search=${searchTerm}`);
                const data = (response.data?.data || response.data || []).map(tx => ({
                    'التاريخ': tx.transaction_date,
                    'رقم العملية': tx.transaction_number || `#TX-${tx.id}`,
                    'صاحب العملية': tx.owner_name,
                    'رقم الحساب': tx.account_number,
                    'البنك': tx.bank_name,
                    'الوسيلة': tx.payment_mode_name || '---',
                    'المبلغ الإجمالي': tx.total_amount,
                    'عدد التقارير': tx.deposit_reports?.length || 0,
                    'المسؤول': tx.user_name || '--'
                }));
                exportToExcel(data, `العمليات_البنكية_${new Date().toISOString().split('T')[0]}`);
            } else {
                const response = await api.get(`/partner-deposits?all=true&search=${searchTerm}`);
                const data = (response.data || []).map(pd => ({
                    'التاريخ': pd.operation_date,
                    'رقم العملية': pd.operation_number || '---',
                    'الشريك': pd.partner?.name,
                    'العقد': pd.contract?.contract_number,
                    'رقم الحساب': pd.account_number,
                    'البنك': pd.bank?.name,
                    'الوسيلة': pd.payment_mode?.name || '---',
                    'المبلغ': pd.amount,
                    'المسؤول': pd.user?.name || '--'
                }));
                exportToExcel(data, `إيداعات_الشركاء_${new Date().toISOString().split('T')[0]}`);
            }
        } catch (error) {
            console.error("Export failed:", error);
        }
    };

    const handlePrintList = async () => {
        setPrintLoading(true);
        try {
            const queryParams = new URLSearchParams({
                all: 'true',
                type: 'deposit',
                date_from: printDates.from,
                date_to: printDates.to
            }).toString();
            
            const res = await api.get(`/bank-transactions?${queryParams}`);
            const data = res.data?.data || res.data || [];
            const sortedTransactions = [...data].sort((a, b) => new Date(a.transaction_date) - new Date(b.transaction_date));
            localStorage.setItem('print_bank_list', JSON.stringify({ 
                transactions: sortedTransactions,
                filters: { date_from: printDates.from, date_to: printDates.to }
            }));
            window.open('/print/bank-list', '_blank');
            setIsPrintModalOpen(false);
        } catch (err) {
            toastError('خطأ أثناء جلب البيانات للطباعة');
        } finally {
            setPrintLoading(false);
        }
    };

    const currentPagination = activeTab === 'reports' ? txPagination : activeTab === 'partners' ? pdPagination : activeTab === 'treasury' ? trPagination : expPagination;
    const isLoading = activeTab === 'reports' ? txLoading : activeTab === 'partners' ? pdLoading : activeTab === 'treasury' ? trLoading : expLoading;

    return (
        <div className="space-y-6 text-right" dir="rtl">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl text-slate-800 flex items-center gap-2">
                        <Landmark className="w-6 h-6 text-primary" />
                        نظام البنك (العمليات المودعة)
                    </h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1">عرض العمليات البنكية التي تم إيداعها وإدارتها</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <Button variant="outline" onClick={handleExportExcel} className="border-slate-200 text-slate-600">
                        <FileSpreadsheet className="w-4 h-4 ml-2 text-green-600" /> تحميل Excel
                    </Button>
                    <Button variant="secondary" onClick={() => setIsPrintModalOpen(true)} isLoading={printLoading} className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200">
                        <Printer className="w-4 h-4 ml-2" /> طباعة قائمة المدفوعات
                    </Button>
                    {canCreate && (
                        <Button onClick={() => setDepositTypeModalOpen(true)} className="shadow-lg shadow-primary/20">
                            <Plus className="w-4 h-4 ml-2" /> عملية جديدة
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <Card className="p-4 bg-white border-slate-100 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-500">
                        <Landmark size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">عدد العمليات</p>
                        <p className="text-lg font-bold text-slate-700">{stats.total_count}</p>
                    </div>
                </Card>
                <Card className="p-4 bg-white border-slate-100 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center text-primary">
                        <CheckCircle2 size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">إجمالي المبالغ</p>
                        <p className="text-lg font-bold text-primary">{parseFloat(stats.total_amount).toLocaleString()} د.م</p>
                    </div>
                </Card>
                <Card className="p-4 bg-white border-slate-100 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                        <CreditCard size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">عمليات بنكية</p>
                        <p className="text-lg font-bold text-slate-700">{parseFloat(stats.bank_operations_amount).toLocaleString()} د.م</p>
                    </div>
                </Card>
                <Card className="p-4 bg-white border-slate-100 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                        <CheckCircle2 size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">المجموع نقداً</p>
                        <p className="text-lg font-bold text-slate-700">{parseFloat(stats.espece_amount).toLocaleString()} د.م</p>
                    </div>
                </Card>
                <Card className="p-4 bg-white border-red-100 flex items-center gap-4 shadow-sm">
                    <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500">
                        <ArrowDownRight size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] text-red-400 uppercase font-bold">إجمالي المصاريف</p>
                        <p className="text-lg font-bold text-red-600">{parseFloat(stats.total_expenses || 0).toLocaleString()} د.م</p>
                    </div>
                </Card>
            </div>

            <Card className="p-4 md:p-6 bg-white/50 backdrop-blur-sm border-slate-100">
                <div className="flex flex-col md:flex-row items-center gap-4 justify-between">
                    <div className="flex bg-slate-100 p-1 rounded-xl">
                        <button 
                            onClick={() => { setActiveTab('reports'); setCurrentPage(1); }}
                            className={`flex items-center justify-center gap-2 px-3 md:px-5 py-2 rounded-lg text-[13px] md:text-sm font-bold whitespace-nowrap transition-all ${activeTab === 'reports' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <FileText size={16} /> تقارير الإيداع
                        </button>
                        <button 
                            onClick={() => { setActiveTab('partners'); setCurrentPage(1); }}
                            className={`flex items-center justify-center gap-2 px-3 md:px-5 py-2 rounded-lg text-[13px] md:text-sm font-bold whitespace-nowrap transition-all ${activeTab === 'partners' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <Briefcase size={16} /> إيداعات الشركاء
                        </button>
                        <button 
                            onClick={() => { setActiveTab('expenses'); setCurrentPage(1); }}
                            className={`flex items-center justify-center gap-2 px-3 md:px-5 py-2 rounded-lg text-[13px] md:text-sm font-bold whitespace-nowrap transition-all ${activeTab === 'expenses' ? 'bg-white text-red-500 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <Receipt size={16} /> المصاريف المسددة
                        </button>
                        <button 
                            onClick={() => { setActiveTab('treasury'); setCurrentPage(1); }}
                            className={`flex items-center justify-center gap-2 px-3 md:px-5 py-2 rounded-lg text-[13px] md:text-sm font-bold whitespace-nowrap transition-all ${activeTab === 'treasury' ? 'bg-white text-emerald-500 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <Landmark size={16} /> تغذية الخزينة
                        </button>
                    </div>
                    <div className="w-full md:w-96">
                        <Input 
                            placeholder="بحث..." 
                            icon={Search} 
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                </div>
            </Card>

            <Card className="overflow-hidden border-slate-100">
                <div className="overflow-x-auto">
                    {activeTab === 'reports' ? (
                        <table className="w-full text-right font-arabic">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">التاريخ</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">رقم العملية</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">صاحب العملية</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">المسؤول</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">رقم الحساب / البنك</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase text-center">الوسيلة</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase text-center">عدد التقارير</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">المبلغ الإجمالي</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase text-center w-20">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {isLoading ? (
                                    <tr><td colSpan="9" className="py-12 text-center animate-pulse">جاري التحميل...</td></tr>
                                ) : transactions?.length === 0 ? (
                                    <tr><td colSpan="9" className="py-12 text-center text-slate-400">لا توجد عمليات بنكية مسجلة</td></tr>
                                ) : (
                                    transactions?.map(tx => (
                                        <tr key={tx.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4 text-sm text-slate-700 font-medium">{tx.transaction_date}</td>
                                            <td className="px-6 py-4"><span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold bg-slate-100 px-2 py-1 rounded">{tx.transaction_number || `#TX-${tx.id}`}</span></td>
                                            <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400"><User size={14} /></div><span className="text-sm text-slate-800 font-bold">{tx.owner_name}</span></div></td>
                                            <td className="px-6 py-4"><div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><User size={10} /></div><span className="text-[11px] text-slate-600 font-medium">{tx.user_name || '--'}</span></div></td>
                                            <td className="px-6 py-4"><div className="flex flex-col"><span className="text-xs text-slate-700">{tx.account_number}</span><span className="text-[10px] text-primary font-bold">{tx.bank_name}</span></div></td>
                                            <td className="px-6 py-4 text-center"><Badge variant={tx.payment_mode_name === 'CHEQUE' ? 'warning' : 'success'}>{tx.payment_mode_name || '---'}</Badge></td>
                                            <td className="px-6 py-4 text-center"><Badge variant="outline" className="px-3">{tx.deposit_reports?.length || 0} تقارير</Badge></td>
                                            <td className="px-6 py-4"><span className="text-sm font-bold text-primary">{parseFloat(tx.total_amount).toLocaleString()} د.م</span></td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button onClick={() => { setEditingTransaction(tx); setIsModalOpen(true); }} className="p-1 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors" title="تعديل"><Edit2 size={14} /></button>
                                                    <button onClick={async () => {
                                                        if(window.confirm('هل أنت متأكد من حذف هذه العملية؟ سيتم إرجاع كافة التقارير والمدفوعات المرتبطة بها إلى حالة "غير مودعة".')) {
                                                            try { await api.delete(`/bank-transactions/${tx.id}`); success('تم الحذف بنجاح'); refreshTx(); refreshPd(); } catch (err) { toastError('خطأ أثناء الحذف'); }
                                                        }
                                                    }} className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="حذف"><Trash2 size={14} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    ) : activeTab === 'partners' ? (
                        <table className="w-full text-right font-arabic">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">التاريخ</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">رقم العملية</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">الشريك</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">رقم الحساب / البنك</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase text-center">الوسيلة</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">المبلغ</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase text-center w-20">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {isLoading ? (
                                    <tr><td colSpan="7" className="py-12 text-center animate-pulse">جاري التحميل...</td></tr>
                                ) : partnerDeposits?.length === 0 ? (
                                    <tr><td colSpan="7" className="py-12 text-center text-slate-400">لا توجد إيداعات للشركاء</td></tr>
                                ) : (
                                    partnerDeposits?.map(pd => (
                                        <tr key={pd.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4 text-sm text-slate-700 font-medium">{pd.operation_date}</td>
                                            <td className="px-6 py-4"><span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold bg-slate-100 px-2 py-1 rounded">{pd.operation_number || '---'}</span></td>
                                            <td className="px-6 py-4"><div className="flex flex-col"><span className="text-sm text-slate-800 font-bold">{pd.partner?.name}</span><span className="text-xs text-slate-500">العقد: {pd.contract?.contract_number}</span></div></td>
                                            <td className="px-6 py-4"><div className="flex flex-col"><span className="text-xs text-slate-700">{pd.account_number}</span><span className="text-[10px] text-primary font-bold">{pd.bank?.name}</span></div></td>
                                            <td className="px-6 py-4 text-center"><Badge variant={pd.payment_mode?.name === 'CHEQUE' ? 'warning' : 'success'}>{pd.payment_mode?.name || '---'}</Badge></td>
                                            <td className="px-6 py-4"><span className="text-sm font-bold text-primary">{parseFloat(pd.amount).toLocaleString()} د.م</span></td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button onClick={() => { setEditingPartnerDeposit(pd); setIsPartnerModalOpen(true); }} className="p-1 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors" title="تعديل"><Edit2 size={14} /></button>
                                                    <button onClick={async () => {
                                                        if(window.confirm('هل أنت متأكد من حذف إيداع الشريك؟')) {
                                                            try { await api.delete(`/partner-deposits/${pd.id}`); success('تم الحذف بنجاح'); refreshPd(); refreshTx(); } catch (err) { toastError('خطأ أثناء الحذف'); }
                                                        }
                                                    }} className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="حذف"><Trash2 size={14} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    ) : activeTab === 'expenses' ? (
                        <table className="w-full text-right font-arabic">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">التاريخ</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">رقم العملية</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">اسم المورد</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">رقم الحساب / البنك</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase text-center">الوسيلة</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">المبلغ الإجمالي</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase text-center w-20">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {isLoading ? (
                                    <tr><td colSpan="7" className="py-12 text-center animate-pulse">جاري التحميل...</td></tr>
                                ) : expenseTransactions?.length === 0 ? (
                                    <tr><td colSpan="7" className="py-12 text-center text-slate-400">لا توجد مصاريف بنكية مسجلة</td></tr>
                                ) : (
                                    expenseTransactions?.map(tx => (
                                        <tr key={tx.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4 text-sm text-slate-700 font-medium">{tx.transaction_date}</td>
                                            <td className="px-6 py-4"><span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold bg-slate-100 px-2 py-1 rounded">{tx.transaction_number || `#TX-${tx.id}`}</span></td>
                                            <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400"><User size={14} /></div><span className="text-sm text-slate-800 font-bold">{tx.expenses?.[0]?.supplier?.name || '---'}</span></div></td>
                                            <td className="px-6 py-4"><div className="flex flex-col"><span className="text-xs text-slate-700">{tx.account_number}</span><span className="text-[10px] text-primary font-bold">{tx.bank_name}</span></div></td>
                                            <td className="px-6 py-4 text-center"><Badge variant={tx.payment_mode_name === 'CHEQUE' ? 'warning' : 'success'}>{tx.payment_mode_name || '---'}</Badge></td>
                                            <td className="px-6 py-4"><span className="text-sm font-bold text-red-500">{parseFloat(tx.total_amount).toLocaleString()} د.م</span></td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button onClick={() => { setEditingExpenseTransaction(tx); setIsExpenseModalOpen(true); }} className="p-1 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors" title="تعديل"><Edit2 size={14} /></button>
                                                    <button onClick={async () => {
                                                        if(window.confirm('هل أنت متأكد من حذف هذه العملية؟ سيتم إرجاع المصاريف المرتبطة بها إلى حالة غير مسددة.')) {
                                                            try { await api.delete(`/bank-transactions/${tx.id}`); success('تم الحذف بنجاح'); refreshExp(); refreshTx(); } catch (err) { toastError('خطأ أثناء الحذف'); }
                                                        }
                                                    }} className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="حذف"><Trash2 size={14} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    ) : activeTab === 'treasury' ? (
                        <table className="w-full text-right font-arabic">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">التاريخ</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">صاحب العملية</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">رقم الشيك</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">البنك / رقم الحساب</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">المبلغ</th>
                                    <th className="px-6 py-4 text-[11px] text-slate-400 uppercase text-center w-20">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {isLoading ? (
                                    <tr><td colSpan="6" className="py-12 text-center animate-pulse">جاري التحميل...</td></tr>
                                ) : treasuryFeeds?.length === 0 ? (
                                    <tr><td colSpan="6" className="py-12 text-center text-slate-400">لا توجد عمليات تغذية مسجلة</td></tr>
                                ) : (
                                    treasuryFeeds?.map(tx => (
                                        <tr key={tx.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4 text-sm text-slate-700 font-medium">{tx.transaction_date}</td>
                                            <td className="px-6 py-4"><span className="text-sm text-slate-800 font-bold">{tx.owner_name}</span></td>
                                            <td className="px-6 py-4"><span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold bg-slate-100 px-2 py-1 rounded">{tx.transaction_number || '---'}</span></td>
                                            <td className="px-6 py-4"><div className="flex flex-col"><span className="text-xs text-slate-700">{tx.account_number}</span><span className="text-[10px] text-primary font-bold">{tx.bank_name}</span></div></td>
                                            <td className="px-6 py-4"><span className="text-sm font-bold text-emerald-500">{parseFloat(tx.total_amount).toLocaleString()} د.م</span></td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button onClick={() => { setEditingTreasury(tx); setIsTreasuryModalOpen(true); }} className="p-1 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors" title="تعديل"><Edit2 size={14} /></button>
                                                    <button onClick={async () => {
                                                        if(window.confirm('هل أنت متأكد من حذف هذه العملية؟')) {
                                                            try { await api.delete(`/bank-transactions/${tx.id}`); success('تم الحذف بنجاح'); refreshTr(); refreshTx(); } catch (err) { toastError('خطأ أثناء الحذف'); }
                                                        }
                                                    }} className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="حذف"><Trash2 size={14} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    ) : null}
                </div>
                {currentPagination && currentPagination.last_page > 1 && (
                    <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                        <div className="text-xs text-slate-500 font-normal">عرض <span className="font-medium text-slate-700">{currentPagination.from}</span> إلى <span className="font-medium text-slate-700">{currentPagination.to}</span> من أصل <span className="font-medium text-slate-700">{currentPagination.total}</span></div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="h-8 px-3 text-xs">السابق</Button>
                            <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.min(prev + 1, currentPagination.last_page))} disabled={currentPage === currentPagination.last_page} className="h-8 px-3 text-xs">التالي</Button>
                        </div>
                    </div>
                )}
            </Card>

            <Modal isOpen={depositTypeModalOpen} onClose={() => setDepositTypeModalOpen(false)} title="اختر نوع العملية" size="2xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 bg-slate-50/50" dir="rtl">
                    <button onClick={() => { setDepositTypeModalOpen(false); setIsModalOpen(true); setEditingTransaction(null); }} className="flex flex-col items-center justify-center text-center gap-4 p-8 rounded-2xl border border-slate-200 bg-white hover:border-primary hover:bg-primary/5 transition-all group">
                        <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-white group-hover:text-primary transition-all">
                            <FileText size={32} />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="font-bold text-slate-800 text-lg group-hover:text-primary transition-colors">إيداع تقارير مسجلة</span>
                            <span className="text-xs text-slate-500 leading-relaxed">تسجيل إيداع بنكي لتقارير الدفع الموجودة مسبقاً</span>
                        </div>
                    </button>
                    
                    <button onClick={() => { setDepositTypeModalOpen(false); setIsPartnerModalOpen(true); setEditingPartnerDeposit(null); }} className="flex flex-col items-center justify-center text-center gap-4 p-8 rounded-2xl border border-slate-200 bg-white hover:border-emerald-500 hover:bg-emerald-50 transition-all group">
                        <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-white group-hover:text-emerald-500 transition-all">
                            <Briefcase size={32} />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="font-bold text-slate-800 text-lg group-hover:text-emerald-600 transition-colors">إيداع جديد لشريك</span>
                            <span className="text-xs text-slate-500 leading-relaxed">تسجيل إيداع مالي خاص بأحد الشركاء المتعاقدين</span>
                        </div>
                    </button>

                    <button onClick={() => { setDepositTypeModalOpen(false); setIsExpenseModalOpen(true); setEditingExpenseTransaction(null); }} className="flex flex-col items-center justify-center text-center gap-4 p-8 rounded-2xl border border-slate-200 bg-white hover:border-red-500 hover:bg-red-50 transition-all group">
                        <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-white group-hover:text-red-500 transition-all">
                            <Receipt size={32} />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="font-bold text-slate-800 text-lg group-hover:text-red-500 transition-colors">تسديد مصاريف</span>
                            <span className="text-xs text-slate-500 leading-relaxed">تسجيل عملية تسديد أو دفع مصاريف من البنك مباشرة</span>
                        </div>
                    </button>

                    <button onClick={() => { setDepositTypeModalOpen(false); setIsTreasuryModalOpen(true); setEditingTreasury(null); }} className="flex flex-col items-center justify-center text-center gap-4 p-8 rounded-2xl border border-slate-200 bg-white hover:border-amber-500 hover:bg-amber-50 transition-all group">
                        <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-white group-hover:text-amber-500 transition-all">
                            <Landmark size={32} />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="font-bold text-slate-800 text-lg group-hover:text-amber-600 transition-colors">تغذية الخزينة</span>
                            <span className="text-xs text-slate-500 leading-relaxed">سحب مبلغ من البنك لتغذية الخزينة أو الصندوق النقدي</span>
                        </div>
                    </button>
                </div>
            </Modal>

            <Modal isOpen={isPrintModalOpen} onClose={() => setIsPrintModalOpen(false)} title="طباعة العمليات البنكية" size="md">
                <div className="space-y-6 text-right" dir="rtl">
                    <p className="text-sm text-slate-500">اختر الفترة الزمنية للعمليات التي تود طباعتها:</p>
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="من تاريخ" type="date" value={printDates.from} onChange={e => setPrintDates({ ...printDates, from: e.target.value })} icon={Calendar} />
                        <Input label="إلى تاريخ" type="date" value={printDates.to} onChange={e => setPrintDates({ ...printDates, to: e.target.value })} icon={Calendar} />
                    </div>
                    <div className="flex justify-end pt-4 border-t border-slate-100">
                        <Button onClick={handlePrintList} isLoading={printLoading} className="px-8"><Printer className="w-4 h-4 ml-2" />تأكيد الطباعة</Button>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setEditingTransaction(null); }} title={editingTransaction ? "تعديل عملية إيداع بنكية" : "تسجيل عملية إيداع بنكية"} size="3xl">
                <BankTransactionForm initialData={editingTransaction} onSuccess={() => { setIsModalOpen(false); setEditingTransaction(null); refreshTx(); refreshPd(); }} />
            </Modal>

            <Modal isOpen={isPartnerModalOpen} onClose={() => { setIsPartnerModalOpen(false); setEditingPartnerDeposit(null); }} title={editingPartnerDeposit ? "تعديل إيداع شريك" : "تسجيل إيداع جديد لشريك"} size="3xl">
                <PartnerDepositForm initialData={editingPartnerDeposit} onSuccess={() => { setIsPartnerModalOpen(false); setEditingPartnerDeposit(null); refreshPd(); refreshTx(); }} />
            </Modal>

            <Modal isOpen={isExpenseModalOpen} onClose={() => { setIsExpenseModalOpen(false); setEditingExpenseTransaction(null); }} title={editingExpenseTransaction ? "تعديل تسديد مصاريف" : "تسديد مصاريف من البنك"} size="3xl">
                <ExpenseTransactionForm initialData={editingExpenseTransaction} onSuccess={() => { setIsExpenseModalOpen(false); setEditingExpenseTransaction(null); refreshExp(); refreshTx(); }} />
            </Modal>

            <Modal isOpen={isTreasuryModalOpen} onClose={() => { setIsTreasuryModalOpen(false); setEditingTreasury(null); }} title={editingTreasury ? "تعديل تغذية الخزينة" : "تغذية الخزينة"} size="2xl">
                <TreasuryFeedForm initialData={editingTreasury} onSuccess={() => { setIsTreasuryModalOpen(false); setEditingTreasury(null); refreshTr(); refreshTx(); }} />
            </Modal>
        </div>
    );
};

const TreasuryFeedForm = ({ onSuccess, initialData }) => {
    const { success, error: toastError } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        transaction_date: initialData?.transaction_date || new Date().toISOString().split('T')[0],
        owner_name: initialData?.owner_name || '',
        account_number: initialData?.account_number || '',
        bank_name: initialData?.bank_name || '',
        transaction_number: initialData?.transaction_number || '',
        total_amount: initialData?.total_amount || '',
        type: 'treasury_feed'
    });

    const [selectedBankId, setSelectedBankId] = useState('');
    const { data: banksResponse } = useGet('/banks');
    const { data: accountsResponse } = useGet('/bank-accounts');
    const { data: usersResponse } = useGet('/users-list');

    const banks = Array.isArray(banksResponse) ? banksResponse : (banksResponse?.data || []);
    const accounts = Array.isArray(accountsResponse) ? accountsResponse : (accountsResponse?.data || []);
    const users = Array.isArray(usersResponse) ? usersResponse : (usersResponse?.data || []);

    useEffect(() => {
        if (initialData && banks.length > 0 && !selectedBankId) {
            const bank = banks.find(b => b.name === initialData.bank_name);
            if (bank) setSelectedBankId(bank.id.toString());
        }
    }, [initialData, banks, selectedBankId]);

    const filteredAccounts = accounts.filter(acc => Number(acc.bank_id) === Number(selectedBankId));

    const handleBankChange = (e) => {
        const bankId = e.target.value;
        setSelectedBankId(bankId);
        const bank = banks.find(b => b.id === parseInt(bankId));
        const bankAccounts = accounts.filter(acc => Number(acc.bank_id) === Number(bankId));
        const firstAccount = bankAccounts.length > 0 ? bankAccounts[0].account_number : '';
        setFormData(prev => ({ ...prev, bank_name: bank?.name || '', account_number: firstAccount }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (initialData) {
                await api.put(`/bank-transactions/${initialData.id}`, formData);
                success('تم تعديل عملية التغذية بنجاح');
            } else {
                await api.post('/bank-transactions', formData);
                success('تم تسجيل التغذية بنجاح');
            }
            onSuccess();
        } catch (err) {
            toastError('خطأ أثناء الحفظ');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6 text-right" dir="rtl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                    label="صاحب العملية"
                    required
                    options={users.map(u => ({ value: u.name, label: u.name }))}
                    value={formData.owner_name}
                    onChange={e => setFormData({...formData, owner_name: e.target.value})}
                    icon={User}
                />
                <Input 
                    label="تاريخ العملية" 
                    type="date" 
                    required 
                    value={formData.transaction_date}
                    onChange={e => setFormData({...formData, transaction_date: e.target.value})}
                    icon={Calendar}
                />
                <Input 
                    label="رقم الشيك" 
                    required 
                    value={formData.transaction_number}
                    onChange={e => setFormData({...formData, transaction_number: e.target.value})}
                    icon={FileText}
                />
                <Input 
                    label="المبلغ" 
                    type="number"
                    step="0.01"
                    required 
                    value={formData.total_amount}
                    onChange={e => setFormData({...formData, total_amount: e.target.value})}
                />
                <Select
                    label="اسم البنك"
                    required
                    options={banks.map(b => ({ value: b.id, label: b.name }))}
                    value={selectedBankId}
                    onChange={handleBankChange}
                    icon={Landmark}
                />
                <Select
                    label="رقم الحساب"
                    required
                    options={filteredAccounts.map(acc => ({ value: acc.account_number, label: acc.account_number }))}
                    value={formData.account_number}
                    onChange={e => setFormData({...formData, account_number: e.target.value})}
                    disabled={!selectedBankId}
                />
            </div>
            <div className="flex justify-end pt-4 border-t border-slate-100">
                <Button type="submit" isLoading={submitting} className="px-10">تأكيد التغذية</Button>
            </div>
        </form>
    );
};

const PartnerDepositForm = ({ onSuccess, initialData }) => {
    const { success, error: toastError } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        partner_id: initialData?.partner_id || '',
        contract_id: initialData?.contract_id || '',
        bank_id: initialData?.bank_id || '',
        account_number: initialData?.account_number || '',
        payment_mode_id: initialData?.payment_mode_id || '',
        operation_date: initialData?.operation_date || new Date().toISOString().split('T')[0],
        operation_number: initialData?.operation_number || '',
        amount: initialData?.amount || ''
    });

    const { data: partnersResponse } = useGet('/partners');
    const { data: banksResponse } = useGet('/banks');
    const { data: accountsResponse } = useGet('/bank-accounts');
    const { data: paymentModesRaw } = useGet('/payment-modes');

    const partners = partnersResponse || [];
    const banks = Array.isArray(banksResponse) ? banksResponse : (banksResponse?.data || []);
    const accounts = Array.isArray(accountsResponse) ? accountsResponse : (accountsResponse?.data || []);
    const paymentModes = Array.isArray(paymentModesRaw) ? paymentModesRaw : (paymentModesRaw?.data || []);

    const selectedPartner = partners.find(p => p.id.toString() === formData.partner_id.toString());
    const filteredContracts = selectedPartner?.contracts?.filter(c => c.status === 'active') || [];
    const filteredAccounts = accounts.filter(acc => Number(acc.bank_id) === Number(formData.bank_id));

    const handleBankChange = (e) => {
        const bankId = e.target.value;
        const bankAccounts = accounts.filter(acc => Number(acc.bank_id) === Number(bankId));
        const firstAccount = bankAccounts.length > 0 ? bankAccounts[0].account_number : '';
        setFormData(prev => ({ ...prev, bank_id: bankId, account_number: firstAccount }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (initialData) {
                await api.put(`/partner-deposits/${initialData.id}`, formData);
                success('تم تعديل إيداع الشريك');
            } else {
                await api.post('/partner-deposits', formData);
                success('تم تسجيل إيداع الشريك');
            }
            onSuccess();
        } catch (err) {
            toastError('خطأ أثناء الحفظ');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6 text-right" dir="rtl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                    label="الشريك"
                    required
                    options={partners.map(p => ({ value: p.id, label: p.name }))}
                    value={formData.partner_id}
                    onChange={e => setFormData({...formData, partner_id: e.target.value, contract_id: ''})}
                    icon={Building}
                />
                <Select
                    label="العقد"
                    required
                    options={filteredContracts.map(c => ({ value: c.id, label: c.contract_number }))}
                    value={formData.contract_id}
                    onChange={e => setFormData({...formData, contract_id: e.target.value})}
                    icon={FileText}
                    disabled={!formData.partner_id}
                />
                <Input 
                    label="تاريخ العملية" 
                    type="date" 
                    required 
                    value={formData.operation_date}
                    onChange={e => setFormData({...formData, operation_date: e.target.value})}
                    icon={Calendar}
                />
                <Input 
                    label="رقم العملية (اختياري)" 
                    value={formData.operation_number}
                    onChange={e => setFormData({...formData, operation_number: e.target.value})}
                    icon={Landmark}
                />
                <Select
                    label="اسم البنك"
                    required
                    options={banks.map(b => ({ value: b.id, label: b.name }))}
                    value={formData.bank_id}
                    onChange={handleBankChange}
                    icon={Landmark}
                />
                <Select
                    label="رقم الحساب"
                    required
                    options={filteredAccounts.map(acc => ({ value: acc.account_number, label: acc.account_number }))}
                    value={formData.account_number}
                    onChange={e => setFormData({...formData, account_number: e.target.value})}
                    disabled={!formData.bank_id}
                />
                <Select
                    label="وسيلة الدفع"
                    required
                    options={paymentModes.map(pm => ({ value: pm.id, label: pm.name }))}
                    value={formData.payment_mode_id}
                    onChange={e => setFormData({...formData, payment_mode_id: e.target.value})}
                    icon={CreditCard}
                />
                <Input 
                    label="القيمة بالدرهم" 
                    type="number"
                    step="0.01"
                    required 
                    value={formData.amount}
                    onChange={e => setFormData({...formData, amount: e.target.value})}
                />
            </div>
            <div className="flex justify-end pt-4 border-t">
                <Button type="submit" isLoading={submitting} className="px-10">تأكيد الإيداع</Button>
            </div>
        </form>
    );
};

const BankTransactionForm = ({ onSuccess, initialData }) => {
    const { success, error: toastError, warning } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        transaction_date: initialData?.transaction_date || new Date().toISOString().split('T')[0],
        owner_name: initialData?.owner_name || '',
        account_number: initialData?.account_number || '',
        bank_name: initialData?.bank_name || '',
        transaction_number: initialData?.transaction_number || '',
        notes: initialData?.notes || '',
        payment_mode_id: initialData?.payment_mode_id || '',
        deposit_report_ids: initialData?.deposit_reports?.map(r => r.id) || []
    });

    const [selectedBankId, setSelectedBankId] = useState('');

    const { data: banksResponse } = useGet('/banks');
    const { data: accountsResponse } = useGet('/bank-accounts');
    const { data: paymentModesRaw } = useGet('/payment-modes');
    const { data: reportsRaw, loading: reportsLoading } = useGet('/deposit-reports/pending-bank');
    const { data: staffResponse } = useGet('/staff-members?all=true');
    const { data: usersResponse } = useGet('/users-list');

    const banks = Array.isArray(banksResponse) ? banksResponse : (banksResponse?.data || []);
    const accounts = Array.isArray(accountsResponse) ? accountsResponse : (accountsResponse?.data || []);
    const paymentModes = Array.isArray(paymentModesRaw) ? paymentModesRaw : (paymentModesRaw?.data || []);
    const users = Array.isArray(usersResponse) ? usersResponse : (usersResponse?.data || []);
    const reports = useMemo(() => {
        const pending = reportsRaw?.data || reportsRaw || [];
        const linked = initialData?.deposit_reports || [];
        const merged = [...linked];
        pending.forEach(p => { if (!merged.some(l => l.id === p.id)) merged.push(p); });
        return merged;
    }, [reportsRaw, initialData]);

    const filteredReports = useMemo(() => {
        let result = reports;
        if (!formData.payment_mode_id) return [];
        if (formData.payment_mode_id) {
            const selectedModeObj = paymentModes.find(pm => pm.id.toString() === formData.payment_mode_id.toString());
            const selectedModeName = selectedModeObj ? selectedModeObj.name : '';
            result = result.filter(r => {
                if (!r.payments || r.payments.length === 0) return true;
                return r.payments.some(p => {
                    const mode = p.payment_mode_details?.name || p.payment_mode;
                    return mode === selectedModeName;
                });
            });
        }
        return result;
    }, [reports, formData.payment_mode_id, paymentModes]);

    const staffMembers = staffResponse?.data || staffResponse || [];

    useEffect(() => {
        if (initialData && banks.length > 0 && !selectedBankId) {
            const bank = banks.find(b => b.name === initialData.bank_name);
            if (bank) setSelectedBankId(bank.id.toString());
        }
    }, [initialData, banks, selectedBankId]);

    const filteredAccounts = accounts.filter(acc => Number(acc.bank_id) === Number(selectedBankId));

    const handleBankChange = (e) => {
        const bankId = e.target.value;
        setSelectedBankId(bankId);
        const bank = banks.find(b => b.id === parseInt(bankId));
        const bankAccounts = accounts.filter(acc => Number(acc.bank_id) === Number(bankId));
        const firstAccount = bankAccounts.length > 0 ? bankAccounts[0].account_number : '';
        setFormData(prev => ({ ...prev, bank_name: bank?.name || '', account_number: firstAccount }));
    };

    const handleCheckboxChange = (id) => {
        setFormData(prev => {
            const current = prev.deposit_report_ids;
            return { ...prev, deposit_report_ids: current.includes(id) ? current.filter(rid => rid !== id) : [...current, id] };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.payment_mode_id) return warning('يرجى اختيار وسيلة الدفع');
        if (formData.deposit_report_ids.length === 0) return warning('يرجى اختيار تقرير إيداع واحد على الأقل');

        setSubmitting(true);
        try {
            if (initialData) {
                await api.put(`/bank-transactions/${initialData.id}`, formData);
                success('تم التحديث بنجاح');
            } else {
                await api.post('/bank-transactions', formData);
                success('تم التسجيل بنجاح');
            }
            onSuccess();
        } catch (err) {
            toastError('خطأ أثناء الحفظ');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6 text-right">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="تاريخ العملية" type="date" required value={formData.transaction_date} onChange={e => setFormData({...formData, transaction_date: e.target.value})} icon={Calendar} />
                <Select label="صاحب العملية" required options={users.map(u => ({ value: u.name, label: u.name }))} value={formData.owner_name} onChange={e => setFormData({...formData, owner_name: e.target.value})} icon={User} />
                <Select label="اسم البنك" required options={banks.map(b => ({ value: b.id, label: b.name }))} value={selectedBankId} onChange={handleBankChange} />
                <Select label="رقم الحساب" required options={filteredAccounts.map(acc => ({ value: acc.account_number, label: acc.account_number }))} value={formData.account_number} onChange={e => setFormData({...formData, account_number: e.target.value})} disabled={!selectedBankId} />
                <Input label="رقم العملية (اختياري)" value={formData.transaction_number} onChange={e => setFormData({...formData, transaction_number: e.target.value})} icon={Landmark} />
                <Select label="وسيلة الدفع *" required options={paymentModes.map(pm => ({ value: pm.id, label: pm.name }))} value={formData.payment_mode_id} onChange={e => setFormData({...formData, payment_mode_id: e.target.value, deposit_report_ids: []})} icon={CreditCard} />
            </div>

            <div className="space-y-3">
                <div className="flex flex-col border-b border-slate-100 pb-2"><h4 className="text-sm font-bold text-slate-700">تقارير الإيداع المتوفرة</h4></div>
                {reportsLoading ? <div className="py-8 text-center animate-pulse text-xs">جاري التحميل...</div> : !formData.payment_mode_id ? <div className="py-8 text-center text-amber-500 font-medium text-sm">يرجى اختيار وسيلة الدفع أولاً</div> : filteredReports.length === 0 ? <div className="py-8 text-center text-xs text-slate-400">لا توجد تقارير إيداع معلقة</div> : (
                    <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                        {filteredReports.map(report => (
                            <div key={report.id} className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer ${formData.deposit_report_ids.includes(report.id) ? 'border-primary bg-primary/5 shadow-sm' : 'border-slate-100 hover:border-slate-300 bg-white'}`} onClick={() => handleCheckboxChange(report.id)}>
                                <div className="flex items-center gap-3">
                                    <input type="checkbox" checked={formData.deposit_report_ids.includes(report.id)} onChange={() => {}} className="w-4 h-4 rounded text-primary border-slate-300 focus:ring-primary" />
                                    <div>
                                        <div className="text-sm font-bold text-slate-800">تقرير #{report.report_number}</div>
                                        <div className="text-[10px] text-slate-500">{report.report_date} • {report.payments_count} مدفوعات</div>
                                    </div>
                                </div>
                                <div className="text-sm text-primary font-bold">{report.total_amount} د.م</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {!formData.payment_mode_id && <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50"><AlertCircle size={14} className="text-amber-500" /><span className="text-xs text-amber-700">يرجى اختيار وسيلة الدفع أولاً</span></div>}
            <div className="flex justify-end pt-4 border-t"><Button type="submit" isLoading={submitting} disabled={formData.deposit_report_ids.length === 0 || !formData.payment_mode_id} className="px-10">تأكيد العملية البنكية</Button></div>
        </form>
    );
};

const ExpenseTransactionForm = ({ onSuccess, initialData }) => {
    const { success, error: toastError, warning } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        type: 'expense',
        transaction_date: initialData?.transaction_date || new Date().toISOString().split('T')[0],
        owner_name: initialData?.owner_name || '',
        account_number: initialData?.account_number || '',
        bank_name: initialData?.bank_name || '',
        transaction_number: initialData?.transaction_number || '',
        notes: initialData?.notes || '',
        payment_mode_id: initialData?.payment_mode_id || '',
        expense_ids: initialData?.expenses?.map(r => r.id) || []
    });

    const [selectedBankId, setSelectedBankId] = useState('');
    const [selectedSupplierId, setSelectedSupplierId] = useState(() => {
        if (initialData?.expenses?.length > 0) {
            return initialData.expenses[0].supplier_id?.toString() || '';
        }
        return '';
    });

    const { data: banksResponse } = useGet('/banks');
    const { data: accountsResponse } = useGet('/bank-accounts');
    const { data: paymentModesRaw } = useGet('/payment-modes');
    const { data: expensesRaw, loading: expensesLoading } = useGet('/expenses/unpaid');
    const { data: staffResponse } = useGet('/staff-members?all=true');
    const { data: suppliersResponse } = useGet('/suppliers');

    const banks = Array.isArray(banksResponse) ? banksResponse : (banksResponse?.data || []);
    const accounts = Array.isArray(accountsResponse) ? accountsResponse : (accountsResponse?.data || []);
    const paymentModes = Array.isArray(paymentModesRaw) ? paymentModesRaw : (paymentModesRaw?.data || []);
    const suppliers = Array.isArray(suppliersResponse) ? suppliersResponse : (suppliersResponse?.data || []);
    const { data: usersResponse } = useGet('/users-list');
    const users = Array.isArray(usersResponse) ? usersResponse : (usersResponse?.data || []);
    const unpaidExpenses = useMemo(() => {
        const pending = expensesRaw?.data || expensesRaw || [];
        const linked = initialData?.expenses || [];
        const merged = [...linked];
        pending.forEach(p => { if (!merged.some(l => l.id === p.id)) merged.push(p); });
        
        if (!selectedSupplierId) return [];
        return merged.filter(exp => exp.supplier_id?.toString() === selectedSupplierId.toString());
    }, [expensesRaw, initialData, selectedSupplierId]);

    const staffMembers = staffResponse?.data || staffResponse || [];

    useEffect(() => {
        if (initialData && banks.length > 0 && !selectedBankId) {
            const bank = banks.find(b => b.name === initialData.bank_name);
            if (bank) setSelectedBankId(bank.id.toString());
        }
    }, [initialData, banks, selectedBankId]);

    const filteredAccounts = accounts.filter(acc => Number(acc.bank_id) === Number(selectedBankId));

    const handleBankChange = (e) => {
        const bankId = e.target.value;
        setSelectedBankId(bankId);
        const bank = banks.find(b => b.id === parseInt(bankId));
        const bankAccounts = accounts.filter(acc => Number(acc.bank_id) === Number(bankId));
        const firstAccount = bankAccounts.length > 0 ? bankAccounts[0].account_number : '';
        setFormData(prev => ({ ...prev, bank_name: bank?.name || '', account_number: firstAccount }));
    };

    const handleCheckboxChange = (id) => {
        setFormData(prev => {
            const current = prev.expense_ids;
            return { ...prev, expense_ids: current.includes(id) ? current.filter(rid => rid !== id) : [...current, id] };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.payment_mode_id) return warning('يرجى اختيار وسيلة الدفع');
        if (formData.expense_ids.length === 0) return warning('يرجى اختيار مصروف واحد على الأقل');

        setSubmitting(true);
        try {
            if (initialData) {
                await api.put(`/bank-transactions/${initialData.id}`, formData);
                success('تم التحديث بنجاح');
            } else {
                await api.post('/bank-transactions', formData);
                success('تم التسجيل بنجاح');
            }
            onSuccess();
        } catch (err) {
            toastError('خطأ أثناء الحفظ');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6 text-right">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="تاريخ العملية" type="date" required value={formData.transaction_date} onChange={e => setFormData({...formData, transaction_date: e.target.value})} icon={Calendar} />
                <Select label="صاحب العملية" required options={users.map(u => ({ value: u.name, label: u.name }))} value={formData.owner_name} onChange={e => setFormData({...formData, owner_name: e.target.value})} icon={User} />
                <Select label="اسم البنك" required options={banks.map(b => ({ value: b.id, label: b.name }))} value={selectedBankId} onChange={handleBankChange} />
                <Select label="رقم الحساب" required options={filteredAccounts.map(acc => ({ value: acc.account_number, label: acc.account_number }))} value={formData.account_number} onChange={e => setFormData({...formData, account_number: e.target.value})} disabled={!selectedBankId} />
                <Input label="رقم العملية (اختياري)" value={formData.transaction_number} onChange={e => setFormData({...formData, transaction_number: e.target.value})} icon={Landmark} />
                <Select label="وسيلة الدفع *" required options={paymentModes.map(pm => ({ value: pm.id, label: pm.name }))} value={formData.payment_mode_id} onChange={e => setFormData({...formData, payment_mode_id: e.target.value})} icon={CreditCard} />
                <Select label="المورد" required options={suppliers.map(sup => ({ value: sup.id, label: sup.name }))} value={selectedSupplierId} onChange={e => { setSelectedSupplierId(e.target.value); setFormData(prev => ({ ...prev, expense_ids: [] })); }} icon={Building} disabled={!!initialData} />
            </div>

            <div className="space-y-3">
                <div className="flex flex-col border-b border-slate-100 pb-2"><h4 className="text-sm font-bold text-slate-700">{initialData ? 'المصاريف المسددة في هذه العملية' : 'المصاريف غير المسددة'}</h4></div>
                {!selectedSupplierId ? <div className="py-8 text-center text-amber-500 font-medium text-sm">يرجى اختيار المورد أولاً</div> : expensesLoading ? <div className="py-8 text-center animate-pulse text-xs">جاري التحميل...</div> : unpaidExpenses.length === 0 ? <div className="py-8 text-center text-xs text-slate-400">لا توجد مصاريف معلقة للمورد المختار</div> : (
                    <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                        {unpaidExpenses.map(expense => (
                            <div key={expense.id} className={`flex items-center justify-between p-3 rounded-xl border ${initialData ? 'border-primary bg-primary/5 shadow-sm' : (formData.expense_ids.includes(expense.id) ? 'border-primary bg-primary/5 shadow-sm cursor-pointer' : 'border-slate-100 hover:border-slate-300 bg-white cursor-pointer')}`} onClick={() => !initialData && handleCheckboxChange(expense.id)}>
                                <div className="flex items-center gap-3">
                                    {!initialData && <input type="checkbox" checked={formData.expense_ids.includes(expense.id)} onChange={() => {}} className="w-4 h-4 rounded text-primary border-slate-300 focus:ring-primary" />}
                                    <div>
                                        <div className="text-sm font-bold text-slate-800">{expense.expense_type?.name}</div>
                                        <div className="text-[10px] text-slate-500">{expense.transaction_date} • {expense.supplier?.name || 'بدون مورد'}</div>
                                    </div>
                                </div>
                                <div className="text-sm text-primary font-bold">{parseFloat(expense.amount).toLocaleString()} د.م</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {!formData.payment_mode_id && <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50"><AlertCircle size={14} className="text-amber-500" /><span className="text-xs text-amber-700">يرجى اختيار وسيلة الدفع أولاً</span></div>}
            {!selectedSupplierId && <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50"><AlertCircle size={14} className="text-amber-500" /><span className="text-xs text-amber-700">يرجى اختيار المورد أولاً</span></div>}
            <div className="flex justify-end pt-4 border-t"><Button type="submit" isLoading={submitting} disabled={formData.expense_ids.length === 0 || !formData.payment_mode_id || !selectedSupplierId} className="px-10">تأكيد تسديد المصاريف</Button></div>
        </form>
    );
};

export default BankList;
